import { supabase } from './client';

export interface Order {
  id: string;
  customer_id: string;
  organization_id: string;
  shop_id?: string | null;
  order_number?: string;
  external_id?: string;
  order_date: string;
  total_amount: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  payment_status: 'unpaid' | 'partial' | 'paid' | 'refunded';
  notes?: string;
  created_at: string;
  updated_at: string;
  currency?: string;
  customer_metadata?: Record<string, any>;
  shipping_metadata?: Record<string, any>;
  order_metadata?: Record<string, any>;
  tracking_number?: string;
  shipping_provider_id?: string;
  shipping_credential_id?: string;
}

export interface OrderInput {
  customer_id: string;
  organization_id: string;
  shop_id?: string | null;
  order_number?: string;
  external_id?: string;
  order_date?: string;
  total_amount: number;
  status?: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  payment_status?: 'unpaid' | 'partial' | 'paid' | 'refunded';
  notes?: string;
  currency?: string;
  customer_metadata?: Record<string, any>;
  shipping_metadata?: Record<string, any>;
  order_metadata?: Record<string, any>;
  tracking_number?: string;
  shipping_provider_id?: string;
  shipping_credential_id?: string;
}

/**
 * Get all orders for a customer
 */
export const getCustomerOrders = async (customerId: string): Promise<Order[]> => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('customer_id', customerId)
      .order('order_date', { ascending: false });

    if (error) {
      console.error('Error fetching customer orders:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getCustomerOrders:', error);
    throw error;
  }
};

/**
 * Get all orders for an organization
 */
export const getOrganizationOrders = async (organizationId: string): Promise<Order[]> => {
  try {
    console.log(`Fetching orders for organization: ${organizationId}`);
    
    if (!organizationId) {
      console.error('No organization ID provided for getOrganizationOrders');
      return [];
    }
    
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('organization_id', organizationId)
      .order('order_date', { ascending: false });

    if (error) {
      console.error('Error fetching organization orders:', error);
      throw error;
    }

    console.log(`Retrieved ${data?.length || 0} orders for organization ${organizationId}`);
    return data || [];
  } catch (error) {
    console.error('Error in getOrganizationOrders:', error);
    throw error;
  }
};

/**
 * Get a single order by ID
 */
export const getOrderById = async (orderId: string): Promise<Order | null> => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('id', orderId)
      .single();

    if (error) {
      console.error('Error fetching order:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in getOrderById:', error);
    throw error;
  }
};

/**
 * Create a new order
 */
export const createOrder = async (order: OrderInput): Promise<Order> => {
  try {
    // Include shipping_metadata if address information exists
    if (order.shipping_metadata === undefined) {
      order.shipping_metadata = {};
    }
    
    const { data, error } = await supabase
      .from('orders')
      .insert({
        ...order,
        order_date: order.order_date || new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating order:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in createOrder:', error);
    throw error;
  }
};

/**
 * Update an existing order
 */
export const updateOrder = async (id: string, updates: Partial<OrderInput>): Promise<Order> => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating order:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateOrder:', error);
    throw error;
  }
};

/**
 * Delete an order
 */
export const deleteOrder = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('orders')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting order:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteOrder:', error);
    throw error;
  }
};
